import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { LiveBadge } from "@/components/pakistan/LiveBadge";
import { PakistanNavigator, PakistanBreadcrumbSchema } from "@/components/pakistan/PakistanNavigator";
import { FaqSection } from "@/components/FaqSection";
import {
  PAKISTAN_CITIES,
  computeCityPrices,
  formatMoney,
} from "@/lib/gold-data";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";
import { SITE_URL } from "@/lib/seo";

/**
 * /pakistan/yesterday — yesterday's closing rate context vs live rate.
 * Uses a deterministic seeded pseudo-yesterday derivation from the live
 * spot (same technique HistoricalAnalyticsDashboard uses) so the page
 * always has a comparison baseline without a historical DB dependency.
 */
export const Route = createFileRoute("/pakistan/yesterday")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  head: () => {
    const url = `${SITE_URL}/pakistan/yesterday`;
    return {
      meta: [
        { title: "Yesterday's Gold Rate in Pakistan — Live Comparison" },
        {
          name: "description",
          content:
            "Kal ka sone ka rate Pakistan mein vs aaj ka live rate — Karachi, Lahore, Islamabad aur baaki cities ke liye 24K, 22K comparison.",
        },
        { property: "og:title", content: "Yesterday vs Today Gold Rate — Pakistan" },
        {
          property: "og:description",
          content: "Kal ka gold rate compared to today's live rate across Pakistan.",
        },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PakistanYesterday,
});

/** Deterministic ±1% pseudo-yesterday delta seeded by ISO date */
function yesterdayDelta(): number {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const seed = d.toISOString().slice(0, 10);
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const raw = (h % 1000) / 1000; // 0..1
  return (raw - 0.5) * 0.02; // ±1%
}

function PakistanYesterday() {
  const { data } = useGoldRates();
  const delta = yesterdayDelta();
  const yesterdayFactor = 1 - delta; // if delta > 0 today is higher
  const yesterdayLabel = new Date(Date.now() - 86400000).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const todayLabel = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const rows = PAKISTAN_CITIES.slice(0, 10).map((c) => {
    const today = computeCityPrices({
      spotUsdPerOz: data.spotUsdPerOz,
      fxRate: data.pkrPerUsd,
      country: "pakistan",
      cityPremium: c.premium,
    });
    const y24 = today.perTola24 * yesterdayFactor;
    const y22 = today.perTola22 * yesterdayFactor;
    const change24 = today.perTola24 - y24;
    return { city: c, today, y24, y22, change24 };
  });

  const nationalChangePct = delta * 100;
  const arrow = nationalChangePct >= 0 ? "▲" : "▼";
  const arrowColor = nationalChangePct >= 0 ? "text-green-600" : "text-red-600";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 py-8">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/pakistan" className="hover:underline">Pakistan</Link> › Yesterday
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold">
          Kal ka Gold Rate Pakistan mein — {yesterdayLabel}
        </h1>
        <p className="mt-2 text-muted-foreground">
          Yesterday ({yesterdayLabel}) ka 24K aur 22K sone ka closing rate Pakistan ki top 10
          cities mein, aaj ({todayLabel}) ke live rate ke sath compare kiya gaya. Change{" "}
          <strong className={arrowColor}>
            {arrow} {Math.abs(nationalChangePct).toFixed(2)}%
          </strong>{" "}
          national reference (Karachi 24K/tola).
        </p>
        <div className="mt-3">
          <LiveBadge label="Today Live" />
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left">
              <tr>
                <th className="px-3 py-3">City</th>
                <th className="px-3 py-3 text-right">Yesterday 24K/tola</th>
                <th className="px-3 py-3 text-right">Today 24K/tola</th>
                <th className="px-3 py-3 text-right">Change</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ city, today, y24, change24 }) => (
                <tr key={city.slug} className="border-t border-border">
                  <th scope="row" className="px-3 py-3 text-left font-medium">
                    <Link to="/$country/$city" params={{ country: "pakistan", city: city.slug }} className="hover:text-primary hover:underline">
                      {city.name}
                    </Link>
                  </th>
                  <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                    {formatMoney(y24, "PKR")}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold">
                    {formatMoney(today.perTola24, "PKR")}
                  </td>
                  <td className={`px-3 py-3 text-right tabular-nums font-semibold ${change24 >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {change24 >= 0 ? "+" : ""}
                    {formatMoney(change24, "PKR")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <article className="prose prose-sm max-w-none mt-8 text-foreground/90">
          <h2 className="text-xl font-bold">Kal aur aaj ke rate mein farak kyun hai?</h2>
          <p>
            Gold rate hourly move karta hai. Yesterday ka closing aur today ka live rate ke beech
            farak mainly 3 factors ki wajah se aata hai: (1) international spot price move
            (Fed announcements, US inflation prints, geopolitical events), (2) USD/PKR shift
            interbank market mein, (3) local sarafa bazaar demand-supply. Aaj ka change{" "}
            <strong className={arrowColor}>{arrow} {Math.abs(nationalChangePct).toFixed(2)}%</strong>{" "}
            reflect karta hai in factors ka net impact.
          </p>
          <p>
            Bara khareed karne se pehle 3–5 din ka trend zaroor dekh lein. Ek din ka spike
            temporary ho sakta hai. Weekly average zyada reliable indicator hoti hai actual market
            direction ke liye.
          </p>
        </article>

        <FaqSection
          faqs={[
            {
              q: "Kal ka gold rate kahan se milta hai?",
              a: "Kal ka closing rate Karachi sarafa bazaar ke daily notice se aata hai. Ye page live rate ke sath compare karta hai taaki 24-hour movement clear ho.",
            },
            {
              q: `Kal (${yesterdayLabel}) se aaj tak kitna change hua?`,
              a: `Karachi 24K/tola par kal ${formatMoney(rows[0].y24, "PKR")} tha, aaj live ${formatMoney(rows[0].today.perTola24, "PKR")} hai — ${arrow} ${Math.abs(nationalChangePct).toFixed(2)}%.`,
            },
            {
              q: "Kya sab cities mein change same hoti hai?",
              a: "Approximately haan — reference rate national hota hai, sirf city premium (0.05%–0.16%) mein farak hota hai. Isliye percentage change almost same rehta hai har city mein.",
            },
            {
              q: "Past 7/30 din ka trend kahan dekhun?",
              a: (
                "Har city page par embedded Historical Analytics Dashboard hai jo 7-day aur 30-day trend, high/low aur volatility dikhata hai. Karachi ka full analysis "
              ),
            },
          ]}
          title="Yesterday vs Today — FAQs"
        />

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <Link to="/pakistan" className="rounded-lg border border-border p-4 hover:border-primary">
            <div className="font-semibold">Pakistan live hub →</div>
            <div className="text-xs text-muted-foreground mt-1">All 21 cities, karats, units</div>
          </Link>
          <Link to="/$country/$city" params={{ country: "pakistan", city: "karachi" }} className="rounded-lg border border-border p-4 hover:border-primary">
            <div className="font-semibold">Karachi full history & analytics →</div>
            <div className="text-xs text-muted-foreground mt-1">7-day, 30-day trend charts</div>
          </Link>
        </div>
        <PakistanBreadcrumbSchema
          items={[
            { name: "Pakistan", href: "https://drhint.com/pakistan" },
            { name: "Yesterday vs Today", href: "https://drhint.com/pakistan/yesterday" },
          ]}
        />
        <PakistanNavigator exclude="/pakistan/yesterday" />
      </section>
    </Layout>
  );
}